<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $tc('messages.questions', 2),
                        href: meta.links.questions_index,
                    },
                    {
                        name: $tc('messages.images', 2),
                        href: null,
                    },
                ]"
            />
        </template>

        <form :action="meta.links.index" method="GET" @submit.prevent="filter">
            <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-12">
                <div class="sm:col-span-2">
                    <label class="block text-sm font-medium leading-5 text-gray-700">{{ $t('messages.search') }}</label>
                    <div class="mt-1 relative rounded-md shadow-sm">
                        <input
                            v-model="form.search"
                            type="text"
                            :placeholder="`${$t('messages.search')}: ${$t('messages.name')}`"
                            class="pr-10 border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm w-full"
                        />
                        <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                            <svg class="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                    fill-rule="evenodd"
                                    d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                                    clip-rule="evenodd"
                                ></path>
                            </svg>
                        </div>
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                    <div class="mt-1">
                        <SubmitFormButton
                            :button-text="$t('messages.filter')"
                            :processing="form.processing"
                            is-filter
                        />
                    </div>
                </div>
            </div>
        </form>

        <div class="mt-3">
            <Dashboard :uppy="uppy" :plugins="['Webcam']" :props="{ theme: 'light' }" />
        </div>

        <div v-if="fileCollections.data.length === 0" class="flex flex-col mt-5">
            <EmptyState picture />
        </div>
        <AppWell v-else class="mt-3">
            <div class="md:grid md:grid-cols-2 lg:grid-cols-4 md:gap-6 mt-5">
                <div v-for="fileCollection in fileCollections.data" :key="fileCollection.id">
                    <AppCard>
                        <div class="p-1">
                            <div class="text-xs">
                                {{ fileCollection.name }}
                            </div>
                            <div class="flex justify-end items-center gap-x-1">
                                <InertiaSquareLinkButton
                                    action="edit"
                                    :href="fileCollection.links.edit"
                                    v-if="fileCollection.can.edit"
                                />
                                <InertiaSquareDeleteButton
                                    :href="fileCollection.links.delete"
                                    :confirm-text="$t('messages.are_you_sure?')"
                                    v-if="fileCollection.can.delete"
                                />
                            </div>
                            <div class="mt-1">
                                <ZoomableImage :src="fileCollection.image_path" class="flex-none">
                                    <img class="" :src="fileCollection.image_path" :alt="fileCollection.name" />
                                </ZoomableImage>
                            </div>
                            <div class="flex justify-end">
                                <InertiaLink :href="fileCollection.links.questions_index">
                                    <BadgePill>
                                        {{ fileCollection.questions_count }}
                                    </BadgePill>
                                </InertiaLink>
                            </div>
                        </div>
                        <div>
                            <CopyrightNotice :info="fileCollection.copyright" />
                        </div>
                    </AppCard>
                </div>
            </div>
        </AppWell>

        <template v-slot:pagination>
            <InertiaPagination :paginator="fileCollections" />
        </template>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import InertiaPagination from '@/Shared/InertiaPagination.vue';
import BadgePill from '@/components/BadgePill.vue';
import EmptyState from '@/components/EmptyState.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';
import ZoomableImage from '@/components/ZoomableImage.vue';
import AppCard from '@/Shared/AppCard.vue';
import AppWell from '@/Shared/AppWell.vue';
import { Dashboard } from '@uppy/vue';

import '@uppy/core/dist/style.css';
import '@uppy/dashboard/dist/style.css';
import Spanish from '@uppy/locales/lib/es_ES';
import XHRUpload from '@uppy/xhr-upload';

import Uppy from '@uppy/core';
import CopyrightNotice from '@/components/CopyrightNotice.vue';
import InertiaSquareLinkButton from '@/components/InertiaSquareLinkButton.vue';
import InertiaSquareDeleteButton from '@/components/InertiaSquareDeleteButton.vue';

export default {
    components: {
        InertiaSquareDeleteButton,
        InertiaSquareLinkButton,
        CopyrightNotice,
        Dashboard,
        AppWell,
        AppCard,
        ZoomableImage,
        SubmitFormButton,
        EmptyState,
        PanelPage,
        InertiaPagination,
        BadgePill,
    },
    layout: Layout,
    name: 'Index',
    props: ['meta', 'fileCollections', 'filters', 'options'],
    data() {
        return {
            form: this.$inertia.form({
                search: this.filters.search,
            }),
        };
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.index);
        },
        getLocale() {
            return this.$i18n.locale;
        },
    },
    computed: {
        uppy: function () {
            const config = {
                autoProceed: false,
                restrictions: {
                    maxFileSize: 20000000,
                    maxNumberOfFiles: 100,
                    allowedFileTypes: ['image/*', '.jpg', '.jpeg'],
                },
            };
            if (['es', 'ca'].includes(this.getLocale())) {
                config.locale = Spanish;
            }

            return new Uppy(config)
                .use(XHRUpload, {
                    endpoint: this.meta.links.store,
                    method: 'POST',
                    formData: true,
                    showProgressDetails: true,
                    fieldName: 'image',
                    headers: {
                        'X-CSRF-TOKEN': this.$page.props.csrf_token,
                        Accept: 'application/json',
                    },
                    getResponseError(responseText) {
                        return new Error(JSON.parse(responseText).message);
                    },
                })
                .on('complete', (result) => {
                    if (result.failed.length === 0) {
                        this.$inertia.reload();
                    }
                });
        },
    },
    beforeUnmount() {
        this.uppy.close({ reason: 'unmount' });
    },
};
</script>
